import React, { useEffect, useRef } from 'react';
import { ExternalLink, Sparkles } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { useAuth } from '../contexts/AuthContext';
import { shouldShowAds } from '../utils/adVisibility';

interface SponsoredContentProps {
  title: string;
  description: string;
  imageUrl?: string;
  adUrl: string;
  ctaText?: string;
  badge?: string;
  variant?: 'card' | 'banner' | 'inline';
  adId?: string; // Unique identifier for tracking
}

export default function SponsoredContent({
  title,
  description,
  imageUrl,
  adUrl,
  ctaText = "Learn More",
  badge = "Sponsored",
  variant = 'card',
  adId = `sponsored-${variant}-${title.slice(0, 10).toLowerCase().replace(/\s+/g, '-')}`
}: SponsoredContentProps) {
  const location = useLocation();
  const { user } = useAuth();
  const adRef = useRef<HTMLDivElement>(null);
  const { trackEvent, trackConversion } = useAnalytics();
  const hasTrackedImpression = React.useRef(false);

  // Check if ads should be shown
  if (!shouldShowAds(user, location.pathname)) {
    return null;
  }

  // Track ad impression when visible
  useEffect(() => {
    if (hasTrackedImpression.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            if (!hasTrackedImpression.current) {
              hasTrackedImpression.current = true;
              trackEvent('ad_impression', {
                ad_id: adId,
                ad_type: 'sponsored_content',
                ad_variant: variant,
                ad_url: adUrl,
                page: window.location.pathname,
              });
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    if (adRef.current) {
      observer.observe(adRef.current);
    }

    return () => {
      if (adRef.current) {
        observer.unobserve(adRef.current);
      }
    };
  }, [adId, variant, adUrl, trackEvent]);

  const handleClick = () => {
    // Track ad click conversion
    trackEvent('ad_click', {
      ad_id: adId,
      ad_type: 'sponsored_content',
      ad_variant: variant,
      ad_url: adUrl,
      cta_text: ctaText,
      page: window.location.pathname,
    });

    // Track as conversion
    trackConversion('ad_click', undefined, 'USD');

    window.open(adUrl, '_blank', 'noopener,noreferrer');
  };

  if (variant === 'banner') {
    return (
      <div 
        ref={adRef}
        className="bg-gradient-to-r from-orange-50 to-purple-50 border-2 border-orange-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all"
        onClick={handleClick}
      >
        <div className="flex items-start gap-4">
          {imageUrl && (
            <img 
              src={imageUrl} 
              alt={title}
              className="w-20 h-20 object-cover rounded-lg"
            />
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs font-semibold text-orange-600 bg-orange-100 px-2 py-1 rounded">
                {badge}
              </span>
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
            <p className="text-gray-600 text-sm mb-3">{description}</p>
            <button className="inline-flex items-center gap-2 text-orange-600 font-semibold hover:text-orange-700">
              {ctaText}
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div 
        ref={adRef}
        className="bg-white border border-gray-200 rounded-lg p-4 cursor-pointer hover:border-orange-300 hover:shadow-md transition-all"
        onClick={handleClick}
      >
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-orange-500" />
          <span className="text-xs font-semibold text-orange-600">{badge}</span>
        </div>
        <h4 className="font-bold text-gray-900 mb-1">{title}</h4>
        <p className="text-sm text-gray-600 mb-2">{description}</p>
        <span className="text-sm text-orange-600 font-semibold inline-flex items-center gap-1">
          {ctaText}
          <ExternalLink className="w-3 h-3" />
        </span>
      </div>
    );
  }

  // Default: card variant
  return (
    <div 
      ref={adRef}
      className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden cursor-pointer hover:shadow-xl transition-all group"
      onClick={handleClick}
    >
      {imageUrl && (
        <div className="relative h-48 overflow-hidden">
          <img 
            src={imageUrl} 
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
          <div className="absolute top-3 right-3">
            <span className="bg-orange-500 text-white text-xs font-semibold px-2 py-1 rounded">
              {badge}
            </span>
          </div>
        </div>
      )}
      <div className="p-5">
        {!imageUrl && (
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-orange-500" />
            <span className="text-xs font-semibold text-orange-600">{badge}</span>
          </div>
        )}
        <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{description}</p>
        <button className="w-full bg-gradient-to-r from-orange-500 to-purple-600 text-white px-4 py-2 rounded-lg font-semibold hover:from-orange-600 hover:to-purple-700 transition-all flex items-center justify-center gap-2">
          {ctaText}
          <ExternalLink className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

